import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plus, Ticket, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { useContactSessions } from '@/hooks/useContactSessions';
import { DEMAND_TYPE_CONFIG, SESSION_STATUS_CONFIG } from '@/hooks/useSupportInbox';
import type { DemandType, SessionStatus } from '@/types/support.types';

interface TicketAssociationBarProps {
  contactId: string | null;
  currentSessionId: string | null;
  currentSessionStatus?: string | null;
  onSelectSession: (sessionId: string) => void;
  onNewTicket: () => void;
  onAssociatePreTicket?: (targetSessionId: string) => void;
  onResolvePreTicket?: () => void;
  isAssociating?: boolean;
  isResolving?: boolean;
}

export function TicketAssociationBar({
  contactId,
  currentSessionId,
  currentSessionStatus,
  onSelectSession,
  onNewTicket,
  onAssociatePreTicket,
  onResolvePreTicket,
  isAssociating,
  isResolving,
}: TicketAssociationBarProps) {
  const { data: sessions } = useContactSessions(contactId);

  if (!contactId || !sessions || sessions.length === 0) return null;

  const activeSessions = sessions.filter(s => s.status !== 'resolved' && s.status !== 'auto_archived');
  const isCurrentPreTicket = currentSessionStatus === 'pre_ticket';
  const classifiedSessions = activeSessions.filter(s => s.status !== 'pre_ticket' && s.id !== currentSessionId);

  const ResolveButton = () => (
    <button
      onClick={onResolvePreTicket}
      disabled={isResolving}
      className="flex items-center gap-1 px-2.5 py-1 h-7 rounded-full text-xs font-medium border border-green-600/40 text-green-700 dark:text-green-400 hover:bg-green-600/10 dark:hover:bg-green-400/10 transition-colors flex-shrink-0 disabled:opacity-50"
    >
      {isResolving ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <CheckCircle className="h-3 w-3" />
      )}
      Resolver
    </button>
  );

  // Current session is a pre-ticket
  if (isCurrentPreTicket) {
    if (classifiedSessions.length === 0) {
      // No other active sessions - show indigo bar
      return (
        <div className="px-4 py-2 bg-indigo-50 dark:bg-indigo-950/30 border-b flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-indigo-500 flex-shrink-0" />
          <span className="text-xs text-indigo-700 dark:text-indigo-300 font-medium flex-1">
            Pré-chamado · Aguardando classificação
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={onNewTicket}
            className="h-7 text-xs rounded-full border-dashed flex-shrink-0"
          >
            <Plus className="h-3 w-3 mr-1" />
            Novo Chamado
          </Button>
          <ResolveButton />
        </div>
      );
    }

    // Has other active sessions - show amber bar with association pills
    return (
      <div className="px-4 py-2 bg-amber-50 dark:bg-amber-950/20 border-b flex items-center gap-2 overflow-x-auto scrollbar-hide">
        <Ticket className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
        <span className="text-xs text-amber-700 dark:text-amber-300 font-medium flex-shrink-0">
          Associar a:
        </span>
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {classifiedSessions.map((session) => {
            const demandLabel = session.demand_type
              ? DEMAND_TYPE_CONFIG[session.demand_type as DemandType]?.label
              : null;
            const statusLabel = SESSION_STATUS_CONFIG[session.status as SessionStatus]?.label;

            return (
              <button
                key={session.id}
                onClick={() => onAssociatePreTicket?.(session.id)}
                disabled={isAssociating}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors flex-shrink-0 bg-card border hover:bg-primary/10 hover:border-primary/30 cursor-pointer disabled:opacity-50"
              >
                {isAssociating ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : null}
                {demandLabel || statusLabel || 'Chamado'}
              </button>
            );
          })}
          <Button
            variant="outline"
            size="sm"
            onClick={onNewTicket}
            className="h-7 text-xs rounded-full border-dashed flex-shrink-0"
          >
            <Plus className="h-3 w-3 mr-1" />
            Novo Chamado
          </Button>
          <ResolveButton />
        </div>
      </div>
    );
  }

  // Not a pre-ticket: show standard bar only if multiple active sessions
  if (activeSessions.length <= 1) return null;

  return (
    <div className="px-4 py-2 bg-amber-50 dark:bg-amber-950/20 border-b flex items-center gap-2 overflow-x-auto scrollbar-hide">
      <Ticket className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
      <div className="flex items-center gap-1.5 flex-1 min-w-0">
        {activeSessions.map((session) => {
          const isCurrent = session.id === currentSessionId;
          const demandLabel = session.demand_type
            ? DEMAND_TYPE_CONFIG[session.demand_type as DemandType]?.label
            : null;
          const statusLabel = SESSION_STATUS_CONFIG[session.status as SessionStatus]?.label;

          return (
            <button
              key={session.id}
              onClick={() => !isCurrent && onSelectSession(session.id)}
              className={cn(
                'flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors flex-shrink-0',
                isCurrent
                  ? 'bg-primary/10 text-primary border border-primary/30'
                  : 'bg-card border hover:bg-muted cursor-pointer'
              )}
            >
              {demandLabel || statusLabel || 'Chamado'}
              {session.status === 'pre_ticket' && (
                <Badge variant="secondary" className="text-[10px] h-4 px-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                  Pré-chamado
                </Badge>
              )}
            </button>
          );
        })}
        <Button
          variant="outline"
          size="sm"
          onClick={onNewTicket}
          className="h-7 text-xs rounded-full border-dashed flex-shrink-0"
        >
          <Plus className="h-3 w-3 mr-1" />
          Novo Chamado
        </Button>
      </div>
    </div>
  );
}
